import { useState, useCallback, useRef, useEffect } from 'react'

// TypeScript types for Web Speech API
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList
  resultIndex: number
}

interface SpeechRecognitionResultList {
  length: number
  item(index: number): SpeechRecognitionResult
  [index: number]: SpeechRecognitionResult
}

interface SpeechRecognitionResult {
  isFinal: boolean
  length: number
  item(index: number): SpeechRecognitionAlternative
  [index: number]: SpeechRecognitionAlternative
}

interface SpeechRecognitionAlternative {
  transcript: string
  confidence: number
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string
  message?: string
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean
  interimResults: boolean
  lang: string
  start(): void
  stop(): void
  abort(): void
  onresult: ((event: SpeechRecognitionEvent) => void) | null
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null
  onend: (() => void) | null
  onstart: (() => void) | null
}

declare global {
  interface Window {
    SpeechRecognition?: new () => SpeechRecognition
    webkitSpeechRecognition?: new () => SpeechRecognition
  }
}

export interface UseSpeechRecognitionOptions {
  onSpeechEnd?: (transcript: string) => void
  silenceTimeout?: number // ms to wait after last speech before triggering onSpeechEnd
  isAvatarSpeaking?: () => boolean // callback to check if avatar is speaking
}

export interface UseSpeechRecognitionReturn {
  isListening: boolean
  transcript: string
  interimTranscript: string
  isSupported: boolean
  startListening: () => void
  stopListening: () => void
  resetTranscript: () => void
  error: string | null
}

export function useSpeechRecognition(options: UseSpeechRecognitionOptions = {}): UseSpeechRecognitionReturn {
  const { onSpeechEnd, silenceTimeout = 1500, isAvatarSpeaking } = options

  const [isListening, setIsListening] = useState(false)
  const [transcript, setTranscript] = useState('')
  const [interimTranscript, setInterimTranscript] = useState('')
  const [error, setError] = useState<string | null>(null)

  const recognitionRef = useRef<SpeechRecognition | null>(null)
  const silenceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const transcriptRef = useRef<string>('')
  const onSpeechEndRef = useRef(onSpeechEnd)

  // Keep callback ref updated
  useEffect(() => {
    onSpeechEndRef.current = onSpeechEnd
  }, [onSpeechEnd])

  const isSupported = typeof window !== 'undefined' &&
    !!(window.SpeechRecognition || window.webkitSpeechRecognition)

  // Clear silence timer
  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current)
      silenceTimerRef.current = null
    }
  }, [])

  // Reference to isAvatarSpeaking callback
  const isAvatarSpeakingRef = useRef(isAvatarSpeaking)
  useEffect(() => {
    isAvatarSpeakingRef.current = isAvatarSpeaking
  }, [isAvatarSpeaking])

  // Start silence timer - will trigger onSpeechEnd after silence
  // If avatar is speaking, wait and retry
  const startSilenceTimer = useCallback(() => {
    clearSilenceTimer()
    silenceTimerRef.current = setTimeout(() => {
      const finalTranscript = transcriptRef.current

      // If avatar is currently speaking, wait and retry
      if (isAvatarSpeakingRef.current?.()) {
        // console.log('[Speech] Avatar is speaking, waiting...')
        startSilenceTimer() // Retry in another silenceTimeout ms
        return
      }

      if (finalTranscript.trim() && onSpeechEndRef.current) {
        // Stop recognition and trigger callback
        if (recognitionRef.current) {
          recognitionRef.current.stop()
        }
        onSpeechEndRef.current(finalTranscript.trim())
      }
    }, silenceTimeout)
  }, [clearSilenceTimer, silenceTimeout])

  // Initialize speech recognition
  useEffect(() => {
    if (!isSupported) return

    const SpeechRecognitionClass = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SpeechRecognitionClass) return

    const recognition = new SpeechRecognitionClass()
    recognition.continuous = true
    recognition.interimResults = true
    recognition.lang = 'en-US'

    recognition.onstart = () => {
      setIsListening(true)
      setError(null)
      transcriptRef.current = ''
    }

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let finalTranscript = ''
      let interim = ''

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]
        if (result.isFinal) {
          finalTranscript += result[0].transcript
        } else {
          interim += result[0].transcript
        }
      }

      if (finalTranscript) {
        const newTranscript = transcriptRef.current + (transcriptRef.current ? ' ' : '') + finalTranscript.trim()
        transcriptRef.current = newTranscript
        setTranscript(newTranscript)
        // Reset silence timer when we get final results
        startSilenceTimer()
      }
      setInterimTranscript(interim)

      // If we have interim results, also reset the timer (user is still speaking)
      if (interim) {
        clearSilenceTimer()
      }
    }

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      clearSilenceTimer()
      if (event.error === 'no-speech') {
        // Ignore no-speech errors - common and not critical
        return
      }
      setError(event.error)
      setIsListening(false)
    }

    recognition.onend = () => {
      setIsListening(false)
      setInterimTranscript('')
      clearSilenceTimer()
    }

    recognitionRef.current = recognition

    return () => {
      clearSilenceTimer()
      recognition.abort()
    }
  }, [isSupported, startSilenceTimer, clearSilenceTimer])

  const startListening = useCallback(() => {
    if (recognitionRef.current && !isListening) {
      setError(null)
      try {
        recognitionRef.current.start()
      } catch (e) {
        // Already started
        void e
      }
    }
  }, [isListening])

  const stopListening = useCallback(() => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop()
    }
  }, [isListening])

  const resetTranscript = useCallback(() => {
    setTranscript('')
    setInterimTranscript('')
  }, [])

  return {
    isListening,
    transcript,
    interimTranscript,
    isSupported,
    startListening,
    stopListening,
    resetTranscript,
    error,
  }
}
